// src/utils/menu/menuLayout.js
// Page geometry for the flower menu, measured from the design files in
// design_intake/001-flower-menu-pdf/. All values are PDF points with the
// origin at the bottom-left of a US Letter page.

export const PAGE = { width: 612, height: 792, margin: 36 };

// The license logo and name sit in a row above the header, which the design does not
// have. Everything the design draws is moved down by this much to make room for it.
export const BRAND_ROW = { height: 26, gap: 8 };
export const CONTENT_SHIFT = BRAND_ROW.height + BRAND_ROW.gap;

export const SECTION = {
    firstBarBottom: 569.621 - CONTENT_SHIFT,   // bottom edge of the first section bar on every page
    barHeight: 18.495,
    firstCardDrop: 53.886,     // bar bottom -> bottom edge of the first card row
    rowPitch: 48.872,
    minRows: 2,                // the five-line price column is as tall as two card rows
    gapAfter: 19.588,          // last card row -> top of the next section bar
};

export const CARD = {
    columns: [113.367, 267.573, 421.78],
    width: 138.82,
    height: 42.6,
};

export const CARDS_PER_ROW = CARD.columns.length;

const rowsNeeded = (cardCount) => Math.ceil(cardCount / CARDS_PER_ROW);

// Bottom edge of a section's content, given where its bar sits and how many card rows it has.
const contentBottom = (barBottom, rows) =>
    barBottom - SECTION.firstCardDrop - (Math.max(rows, SECTION.minRows) - 1) * SECTION.rowPitch;

// Most card rows a section can have when it starts at the top of a page.
export const MAX_ROWS_PER_PAGE =
    Math.floor((SECTION.firstBarBottom - SECTION.firstCardDrop - PAGE.margin) / SECTION.rowPitch) + 1;

/**
 * Place sections onto pages. A section is kept whole: if it does not fit in the
 * space left on a page it moves to the next one. A section taller than a full
 * page is split into page-sized parts, each repeating the section bar and prices.
 *
 * @param {Array} sections - From buildMenuModel().sections.
 * @returns {Array<{ sections: Array }>} Pages; each placed section adds `barBottom`
 *   and, per card, `x` and `y` (bottom-left corner of the card).
 */
export function paginateMenu(sections) {
    const pages = [];
    let page = null;
    let barBottom = SECTION.firstBarBottom;

    const startPage = () => {
        page = { sections: [] };
        pages.push(page);
        barBottom = SECTION.firstBarBottom;
    };

    for (const section of sections || []) {
        const cardsPerPart = MAX_ROWS_PER_PAGE * CARDS_PER_ROW;
        for (let start = 0; start < section.cards.length; start += cardsPerPart) {
            const cards = section.cards.slice(start, start + cardsPerPart);
            const rows = rowsNeeded(cards.length);

            const fits = contentBottom(barBottom, rows) >= PAGE.margin;
            if (!page || (!fits && page.sections.length > 0)) startPage();

            const firstRowY = barBottom - SECTION.firstCardDrop;
            page.sections.push({
                ...section,
                barBottom,
                cards: cards.map((card, i) => ({
                    ...card,
                    x: CARD.columns[i % CARDS_PER_ROW],
                    y: firstRowY - Math.floor(i / CARDS_PER_ROW) * SECTION.rowPitch,
                })),
            });
            barBottom = contentBottom(barBottom, rows) - SECTION.gapAfter - SECTION.barHeight;
        }
    }

    if (pages.length === 0) startPage();
    return pages;
}
